import { DeviceProtocol } from '@hid/device/device_model'
import { delay, makeData64WithCRC } from '@libs/utils/tools'
import { MagnetDriver } from './device'
import { DataDeadZone, postMagnetKeyInfo } from '@libs/types/response'
import { KK } from '@hid/define/define'
import { KeyCode, KeyFunction, OutputMode, SetMagnetSenior, VK } from '@hid/define/define'
import { MagnetSeniorType } from '@libs/types/enum'
import { computeTravelsWithDeadzone, computeTravelsWithDeadzoneGet, joinBytes, raw, splitIntoBytes } from './tool'
import { allKeysForDevice, HIDFromVk } from '@hid/define/define_fn'
import { deviceManager } from '@hid/device/device_manager'
import { na87Driver } from './na87'
import { Mu68Handle } from './magnet_handler'
import firmware from '@/upgrade/firmware'

// 读 写
export enum ReadWrite {
  Read = 0x00,
  Write = 0x01,
}
let shouldStop = false
enum calibrateType {
  start = 0x0c,
  end = 0x0d,
}
enum datatype {
  bit_8 = 0x01,
  bit_16 = 0x02,
}
enum matrix6_12 {
  hall = 0x02,
  flux = 0x06,
}
export class Magnet extends MagnetDriver {
  private receive = 0
  private collectDataList = [
    ...this.collectData(matrix6_12.hall), // 行程
    ...this.collectData(matrix6_12.flux), // 磁通量
  ]
  private handlers: Mu68Handle[] = []

  constructor(
    private DeviceHID: DeviceProtocol,
    private parent: na87Driver,
  ) {
    super()
    this.handlers = [new Mu68Handle()]
    this.DeviceHID.onReciveAny((data) => {
      this.handlers.forEach((handler) => {
        this.receive++
        if (shouldStop) return
        handler.handle(data)
      })
    })
  }

  private stopLoopAsk() {
    shouldStop = true
  }
  private startLoopAsk() {
    shouldStop = false
  }
  async startCalibration() {
    await this.parent.pauseMusicCapture()
    this.toggleCalibration(calibrateType.start)
  }
  async endCalibration() {
    await this.toggleCalibration(calibrateType.end)
    await delay(100)
    await this.parent.continueMusicCapture()
  }
  async startTravels() {
    await this.parent.pauseMusicCapture()
    this.startLoopAsk()
    this.loopAsk()
  }
  async endTravels() {
    await this.stopLoopAsk()
    this.handlers.forEach((handler) => {
      handler.clear()
    })
    await delay(100)
    await this.parent.continueMusicCapture()
  }
  private async toggleCalibration(type: calibrateType): Promise<number[]> {
    const data = makeData64WithCRC([0x5c, 0x03, 0x00, 0x00, type, 0xff, 0xff])
    const res = await this.DeviceHID.send(data)
    if (res[4] != 0x00) {
      throw Error(`${type == calibrateType.start ? 'start' : 'end'} calibration failed: 0x${res[4].toString(16)}`)
    }
    return res
  }
  private async loopAsk() {
    for (let time = 0; true; time++) {
      if (shouldStop) {
        return
      }
      for (let index = 0; index < this.collectDataList.length; index++) {
        if (shouldStop) {
          return
        }
        const item = this.collectDataList[index]
        await this.DeviceHID.send(item)
        //  TODO: 此处不应该判断是否成功
        // 当按住"0" 时开始行程校准，res[4] 的数据不是0x00
        // if (res[4] != 0) {
        //     throw new Error(`ask failed:${res[4]}`);
        // }
        while (this.receive < 3) {
          await delay(5)
        }
        this.receive = 0
      }
    }
  }

  private collectData(type: matrix6_12): number[][] {
    return [this.createDataPacket(type, datatype.bit_8), this.createDataPacket(type, datatype.bit_16)]
  }

  private createDataPacket(type: matrix6_12, datatype: datatype): number[] {
    return makeData64WithCRC([0x5c, 0x04, 0x12, 0x00, type, datatype, 0xff, 0xff])
  }

  //获取/设置, 多个按键的"单键信息"
  async getKeysTravels(keys?: KK[]): Promise<postMagnetKeyInfo[]> {
    await this.parent.pauseMusicCapture()
    if (!keys) {
      keys = allKeysForDevice(deviceManager.deviceID)
    }

    const data1 = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x04))
    const data2 = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x14)) //RT
    const data3 = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x15)) //RT
    const dataTypes = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x08)) //按键的行程类型,RT需要为2
    const dataGlobalHex = await this.DeviceHID.send([0x5c, 0x09, 0x29, 0xc3])
    const travelGlobal = (dataGlobalHex[8] << 8) + dataGlobalHex[7]

    const respMap = new Map<KK, postMagnetKeyInfo>()
    keys.forEach((key) => {
      if (!dataTypes.has(key)) return
      const one = {
        Key: key,
        KeyType: 0,
        PressNum: 0,
        ReleaseNum: 0,
        QuickTriggerPressNum: 0,
        QuickTriggerReleaseNum: 0,
      }
      const dt = dataTypes.get(key)![0]
      one.KeyType = dt % 0x10
      if ((dt & 0xf0) == 0x20) {
        if (data2.has(key)) {
          one.QuickTriggerPressNum = (data2.get(key)![1] << 8) + data2.get(key)![0]
        }
        if (data3.has(key)) {
          one.QuickTriggerReleaseNum = (data3.get(key)![1] << 8) + data3.get(key)![0]
        }
      }
      if (dt != 0x00) {
        if (data1.has(key)) {
          one.PressNum = (data1.get(key)![1] << 8) + data1.get(key)![0]
          one.ReleaseNum = (data1.get(key)![1] << 8) + data1.get(key)![0]
        }
      } else {
        one.PressNum = travelGlobal
        one.ReleaseNum = travelGlobal
      }
      respMap.set(key, one)
    })
    const resp = Array.from(respMap.values())
    const resp2 = computeTravelsWithDeadzoneGet(resp, this.parent.save.MagnetTravel, this.parent.save.DeadZone)
    await this.parent.continueMusicCapture()
    return resp2
  }

  async resetTravels(dataKeyType: Map<KK, [number, number]>) {
    const data = new Map<KK, number[]>()
    dataKeyType.forEach((value, key) => {
      const fnk = value[0] % 0x10
      data.set(key, [0x08, fnk, 0x00])
    })
    await this.parent.cmdKey(0x23, data, true)
  }

  async syncKeysTravels() {
    await this.parent.pauseMusicCapture()
    const keys = allKeysForDevice(deviceManager.deviceID)
    const dataKeyType = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(keys, 0x08))

    if (this.parent.changeProfileFlag) {
      await this.resetTravels(dataKeyType)
    }

    const magnetTravel = computeTravelsWithDeadzone(this.parent.save.MagnetTravel, this.parent.save.DeadZone)

    const data1 = new Map<KK, number[]>() //普通行程
    const data2 = new Map<KK, number[]>() //开启RT
    const data3 = new Map<KK, number[]>() //关闭RT

    //TODO 优化, 已经是指定类型的按键, 就不需要再次改变了

    magnetTravel.forEach((value, key) => {
      if (!dataKeyType.has(key)) return
      const dt = dataKeyType.get(key)![0]
      const fnk = dt % 0x10
      if (value.QuickTriggerReleaseNum == 0 && value.QuickTriggerPressNum == 0) {
        const tmp = [
          0x08,
          0x10 + fnk,
          0x00, //关闭RT行程
        ]
        data3.set(key, tmp)
      } else {
        const tmp = [
          0x08,
          0x20 + fnk,
          0x00,
          key,
          0x14,
          value.QuickTriggerPressNum & 0xff,
          (value.QuickTriggerPressNum >> 8) & 0xff, //快速触发
          key,
          0x15,
          value.QuickTriggerReleaseNum & 0xff,
          (value.QuickTriggerReleaseNum >> 8) & 0xff, //快速释放
        ]
        data2.set(key, tmp)
      }
      data1.set(key, [0x04, value.PressNum & 0xff, (value.PressNum >> 8) & 0xff])
    })
    await this.parent.cmdKey(0x23, data1, true)
    await this.parent.cmdKey(0x23, data2, true)
    await this.parent.cmdKey(0x23, data3, true)

    await this.parent.continueMusicCapture()
  }

  async syncSeniorkey() {
    await this.parent.pauseMusicCapture()
    const dksData: { [index: KK]: SetMagnetSenior } = {}
    const mtData: { [index: KK]: SetMagnetSenior } = {}
    const tglData: { [index: KK]: SetMagnetSenior } = {}
    const mptData: { [index: KK]: SetMagnetSenior } = {}
    const endData: { [index: KK]: SetMagnetSenior } = {}
    const kmeData: { [index: KK]: SetMagnetSenior } = {}
    const rsData: { [index: KK]: SetMagnetSenior } = {}

    this.parent.save.Keys.forEach(async (value) => {
      if (value.Output.Mode == OutputMode.Magnet) {
        const magnetData = this.parent.save.MagnetData.get(value.Output.DataIndex)
        if (magnetData) {
          switch (magnetData.KeyType) {
            case MagnetSeniorType.SeniorKeyTypeDKS:
              dksData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeMT:
              mtData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeTGL:
              tglData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeMPT:
              mptData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeEND:
              endData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeKME:
              kmeData[value.Input.KK] = magnetData
              break
            case MagnetSeniorType.SeniorKeyTypeRS:
              rsData[value.Input.KK] = magnetData
          }
        }
      }
    })
    if (Object.keys(dksData).length > 0) {
      await this.setDKS(dksData)
    }
    if (Object.keys(mtData).length > 0) {
      await this.setMT(mtData)
    }
    if (Object.keys(tglData).length > 0) {
      await this.setTGL(tglData)
    }
    if (Object.keys(mptData).length > 0) {
      await this.setMPT(mptData)
    }
    if (Object.keys(endData).length > 0) {
      await this.setEND(endData)
    }
    if (Object.keys(kmeData).length > 0) {
      await this.setKME(kmeData)
    }
    if (Object.keys(rsData).length > 0) {
      await this.setRS(rsData)
    }
    await this.clearKmeBind()
    await this.parent.continueMusicCapture()
  }

  async clearKmeBind() {
    const keys = this.parent.resetKmeKeys
    const kks = keys.map((item) => item.Input.KK)
    const keysType = await this.parent.cmdKey(0x23, this.parent.makeCMDSelectArg(kks, 0x08))
    const resetSenior = new Map<KK, number[]>()

    console.log('clearKmeBind', keys)
    keys.forEach((value) => {
      const travel = keysType.get(value.Input.KK)![0] & 0b11110000
      resetSenior.set(value.Input.KK, [8, travel, 0])
    })

    if (resetSenior.size > 0) await this.parent.cmdKey(0x23, resetSenior, true)
    this.parent.clearKmeKeys()
  }

  private async setDKS(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    const fwGTE20250326 = await firmware.versionIs20250326()
    if (fwGTE20250326) {
      this.setDKSFirmwareVersion20250326(data, group, keys)
      for (const [key, value] of group) {
        await this.parent.cmdKey(0x26, new Map([[key, value]]), true)
      }
      return
    }
    const fwGTE104 = await firmware.fwVersionGreaterThanOrEqual('1.0.4', firmware.fwVersionDvs1)
    const fwGTE100 = await firmware.fwVersionGreaterThanOrEqual('1.0.0', firmware.fwVersionDvs2)
    const fwGTE101 = await firmware.versionIs20250326()

    let fwVersionIs104 = false
    if (fwGTE104 || fwGTE100 || fwGTE101) {
      fwVersionIs104 = true
    }

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index] as KK
      const value = data[key]

      let keyParam = new Array(10).fill(0x00)

      if (fwVersionIs104) {
        keyParam = new Array(12).fill(0x00)
      }
      for (let index = 0; index < value.Keys.length; index++) {
        const magenetValue = value.Keys[index]
        if (magenetValue.KeyCode.length > 0) {
          keyParam[0 + index] = this.getMagenetKeyCode(magenetValue.KeyCode)
          keyParam[4 + index] = this.setTrPSx(magenetValue.KeyFunction)
        }
      }

      const [lowByte, hightByte] = splitIntoBytes(value.Keys[0].Travel * 100)
      keyParam[8] = lowByte
      keyParam[9] = hightByte

      if (fwVersionIs104) {
        const [bottomLowByte, bottomHightByte] = splitIntoBytes(value.Keys[1].Travel * 100)
        keyParam[10] = bottomLowByte
        keyParam[11] = bottomHightByte
      }

      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x26, group, true)
  }

  private setDKSFirmwareVersion20250326(data: { [index: KK]: SetMagnetSenior }, group: Map<KK, number[]>, keys: KK[]): Map<KK, number[]> {
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index] as KK
      const value = data[key]
      const keyParam = new Array(16).fill(0x00)
      for (let index = 0; index < value.Keys.length; index++) {
        const magnetValue = value.Keys[index]
        if (magnetValue.KeyCode.length > 0) {
          const [lowByte, hightByte] = splitIntoBytes(this.getMagenetKeyCode(magnetValue.KeyCode))
          const byteIndex = index * 2
          keyParam[byteIndex] = lowByte
          keyParam[byteIndex + 1] = hightByte
          keyParam[8 + index] = this.setTrPSx(magnetValue.KeyFunction)
        }
      }
      const [lowByte, hightByte] = splitIntoBytes(value.Keys[0].Travel * 100)
      keyParam[12] = lowByte
      keyParam[13] = hightByte
      const [bottomLowByte, bottomHightByte] = splitIntoBytes(value.Keys[1].Travel * 100)
      keyParam[14] = bottomLowByte
      keyParam[15] = bottomHightByte
      group.set(key, keyParam)
    }

    return group
  }

  private async setMT(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    const fwGTE20250326 = await firmware.versionIs20250326()
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      if (fwGTE20250326) {
        const keyParam = this.setMTFirmwareVersion20250326(value)
        group.set(key, keyParam)
      } else {
        const keyParam = new Array(3).fill(0xff)
        keyParam[0] = this.getMagenetKeyCode(value.Keys[0].KeyCode)
        keyParam[1] = this.getMagenetKeyCode(value.Keys[1].KeyCode)
        keyParam[2] = value.MTDuration
        group.set(key, keyParam)
      }
    }
    this.parent.cmdKey(0x24, group, true)
  }

  private setMTFirmwareVersion20250326(value: SetMagnetSenior): number[] {
    const keyParam = new Array(5).fill(0xff)
    const [lowByte, hightByte] = splitIntoBytes(this.getMagenetKeyCode(value.Keys[0].KeyCode))
    keyParam[0] = lowByte
    keyParam[1] = hightByte
    const [lowByte1, hightByte1] = splitIntoBytes(this.getMagenetKeyCode(value.Keys[1].KeyCode))
    keyParam[2] = lowByte1
    keyParam[3] = hightByte1
    keyParam[4] = value.MTDuration
    return keyParam
  }

  private async setTGL(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    const fwGTE20250326 = await firmware.versionIs20250326()

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      if (fwGTE20250326) {
        const keyParam = this.setTGLFirmwareVersion20250326(value)
        group.set(key, keyParam)
      } else {
        const keyParam = new Array(2).fill(0xff)
        keyParam[0] = this.getMagenetKeyCode(value.Keys[0].KeyCode)
        //keyParam[1] = value.MTDuration
        keyParam[1] = 0x14
        group.set(key, keyParam)
      }
    }
    this.parent.cmdKey(0x25, group, true)
  }
  private setTGLFirmwareVersion20250326(value: SetMagnetSenior): number[] {
    const keyParam = new Array(4).fill(0xff)
    const [lowByte, hightByte] = splitIntoBytes(this.getMagenetKeyCode(value.Keys[0].KeyCode))
    keyParam[0] = lowByte
    keyParam[1] = hightByte
    keyParam[2] = 0x14
    return keyParam
  }

  private async setMPT(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]

    const fwGTE20250326 = await firmware.versionIs20250326()
    if (fwGTE20250326) {
      this.setMPTFirmwareVersion20250326(group, keys, data)
      for (const [key, value] of group) {
        await this.parent.cmdKey(0x27, new Map([[key, value]]), true)
      }
      return
    }

    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(9).fill(0xff)
      for (let index = 0; index < value.Keys.length; index++) {
        keyParam[0 + index] = this.getMagenetKeyCode(value.Keys[index].KeyCode)

        keyParam[3 + index * 2] = value.Keys[index].Travel & 0xff
        keyParam[3 + index * 2 + 1] = (value.Keys[index].Travel >> 8) & 0xff
      }
      group.set(key, keyParam)
    }
    this.parent.cmdKey(0x27, group, true)
  }
  private setMPTFirmwareVersion20250326(group: Map<KK, number[]>, keys: KK[], data: { [index: KK]: SetMagnetSenior }) {
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(12).fill(0x00)
      for (let index = 0; index < value.Keys.length; index++) {
        const keyCode = this.getMagenetKeyCode(value.Keys[index].KeyCode)
        const [lowByte, hightByte] = splitIntoBytes(keyCode)
        keyParam[0 + index * 2] = lowByte
        keyParam[0 + index * 2 + 1] = hightByte

        keyParam[6 + index * 2] = value.Keys[index].Travel & 0xff
        keyParam[6 + index * 2 + 1] = (value.Keys[index].Travel >> 8) & 0xff
      }
      group.set(key, keyParam)
    }
  }

  private async setEND(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]

      const keyParam = new Array(4).fill(0)
      keyParam[0] = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      const [lowByte, hightByte] = splitIntoBytes(value.DelayTime)
      keyParam[2] = lowByte
      keyParam[3] = hightByte

      group.set(key, keyParam)
    }
    group.forEach((value, key) => {
      const data: Map<KK, number[]> = new Map()
      data.set(key, value)
      this.parent.cmdKey(0x28, data, true)
    })
  }

  private async setKME(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    const isVersion20241125a = await firmware.versionIs20241125a()
    const isVersion20250326 = await firmware.versionIs20250326()
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      const setValue = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      if (isVersion20250326) {
        const keyParam = new Array(58).fill(0x00)
        keyParam[0] = setValue
        keyParam[6] = this.KMEModeConversion(data[key].KMEMode)
        group.set(key, keyParam)
      } else if (isVersion20241125a) {
        let kmeMode = 0
        if (data[key].KMEMode != undefined && data[key].KMEMode.length > 0) {
          kmeMode = data[key].KMEMode[0]
        }
        group.set(key, [setValue, kmeMode])
      } else {
        group.set(key, [setValue])
      }
    }
    await this.parent.cmdKey(0x2c, group, true)
  }

  // KEM Mode 转换
  private KMEModeConversion(KMEMode: number[]): number {
    const [a = 0, b = 0] = KMEMode
    if (a == 0 && b == 1) {
      return 1
    } else if (a == 1 && b == 0) {
      return 2
    } else if (a == 1 && b == 1) {
      return 3
    }
    return 0
  }
  private async setRS(data: { [index: KK]: SetMagnetSenior }) {
    const group: Map<KK, number[]> = new Map()
    const keys: KK[] = Object.keys(data).map(Number) as KK[]
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      const value = data[key]
      const setValue = this.getMagenetKeyCode(value.Keys[0].KeyCode)
      group.set(key, [setValue])
    }
    this.parent.cmdKey(0x2d, group, true)
  }

  private getMagenetKeyCode(data: KeyCode) {
    if (data.length > 0 && data[0] > 0) {
      const code = HIDFromVk(data[0] as VK)
      if (code) {
        return code
      }
      throw new Error('未找到对应的按键值')
    }
    return 0xff
  }

  private setTrPSx(data: KeyFunction) {
    let res = 0
    for (let index = 0; index < data.length; index++) {
      if (index == 0) {
        if (data[index] == 1) {
          res |= 0x01 << 0
        }
        if (data[index] == 2) {
          res |= 0x01 << 1
        }
      } else if (index == 3) {
        if (data[index] > 0) {
          res |= 0x01 << 7
        }
      } else if (index == 1) {
        if (data[index] == 1) {
          res |= 0x01 << 2
        }
        if (data[index] == 2) {
          res |= 0x01 << 2
          res |= 0x01 << 3
          res |= 0x01 << 4
        }
        if (data[index] == 3) {
          res |= 0x01 << 2
        }
      } else if (index == 2) {
        if (data[index] == 1) {
          res |= 0x01 << 5
        }
        if (data[index] == 2 || data[index] == 3) {
          res |= 0x01 << 5
          res |= 0x01 << 6
        }
      }
    }
    return res
  }

  //[顶部死区, 底部死区]
  //写入时, 如果传-1, 则不写入
  async readOrSetDeadZone(isWrite: boolean, value?: [number, number]): Promise<[number, number]> {
    const preData = [0x5c, 0x0f, 0x29, 0, 0]
    const data = makeData64WithCRC(preData)
    const src = await this.parent.DeviceHID.send(data)
    if (src[4] != 0) throw new Error('read deadzone failed')
    const dzSrc = [(src[10] << 8) + src[9], (src[12] << 8) + src[11]] as [number, number]
    if (!isWrite) return dzSrc
    if (!value || value.length != 2) throw new Error('value format error')
    const set = [...preData, ...raw(src).slice(5, 5 + 10)]
    if (value[0] >= 0) {
      set[9] = value[0] & 0xff
      set[10] = (value[0] >> 8) & 0xff
    }
    if (value[1] >= 0) {
      set[11] = value[1] & 0xff
      set[12] = (value[1] >> 8) & 0xff
    }
    set[4] = 1
    const dst = await this.parent.DeviceHID.send(makeData64WithCRC(set))
    if (src[4] != 0) throw new Error('set deadzone failed')
    const dstResp = [(dst[10] << 8) + dst[9], (dst[12] << 8) + dst[11]] as [number, number]
    return dstResp
  }

  private async debugGetKBDefaultValueFnLayout() {
    let keys = allKeysForDevice(deviceManager.deviceID)
    const post = new Map<KK, number[]>()
    keys.forEach(async (key) => {
      post.set(key, [0x01, 0xff, 0xff])
    })
    const data = await this.parent.cmdKey(0x23, post)
    let out = ''
    data.forEach((value, key) => {
      if (value[0] == 0x01) return
      out += `[${key}, [${value[0]}, ${value[1]}]], \n`
    })
    console.log('debug:', out)
  }

  private SetTopDeadZone = 0x16
  private SetBottomDeadZone = 0x17

  async syncKeysDeadZone() {
    await this.parent.pauseMusicCapture()
    const deadZoneList = this.parent.save.DeadZoneList

    const setTopMap = new Map<KK, number[]>()
    const setBottomMap = new Map<KK, number[]>()

    deadZoneList.forEach((value, key) => {
      setTopMap.set(key, [this.SetTopDeadZone, value.Top & 0xff, value.Top >> 8])
      setBottomMap.set(key, [this.SetBottomDeadZone, value.Bottom & 0xff, value.Bottom >> 8])
    })
    await this.parent.cmdKey(0x23, setTopMap, true)
    await this.parent.cmdKey(0x23, setBottomMap, true)
    await this.parent.continueMusicCapture()
  }

  async getKeysDeadZone(keys?: KK[]): Promise<Map<KK, DataDeadZone>> {
    await this.parent.pauseMusicCapture()
    if (!keys) {
      keys = allKeysForDevice(deviceManager.deviceID)
    }
    const getTop = new Map<KK, number[]>()
    const getBottom = new Map<KK, number[]>()
    keys.forEach((key) => {
      getTop.set(key, [this.SetTopDeadZone, 0xff, 0xff])
      getBottom.set(key, [this.SetBottomDeadZone, 0xff, 0xff])
    })
    const topData = await this.parent.cmdKey(0x23, getTop, false)
    const bottomData = await this.parent.cmdKey(0x23, getBottom, false)

    let res = new Map<KK, DataDeadZone>()
    keys.forEach((key) => {
      const keyTopArr = topData.get(key)
      const keyBottomArr = bottomData.get(key)
      if (keyTopArr == undefined || keyBottomArr == undefined) {
        throw new Error('keyTopDeadZone or keyBottomDeadZone is undefined')
      }
      const keyTop = joinBytes([keyTopArr[1], keyTopArr[0]])
      const keyBottom = joinBytes([keyBottomArr[1], keyBottomArr[0]])
      res.set(key, { Top: keyTop, Bottom: keyBottom })
    })
    await this.parent.continueMusicCapture()
    return res
  }

  //测试用
  async DemoDebug() {
    this.debugGetKBDefaultValueFnLayout()
    console.log('debug:', 'end')
  }

  destroy() {}
}
